// The one place that calls upstream's table helpers. The row and column functions were exported
// as `…__EXPERIMENTAL` for years and those names are deprecated aliases in 0.51; if they move
// again, this file is what changes. Everything here must run inside an update.
import {
  $computeTableMapSkipCellCheck,
  $createTableNodeWithDimensions,
  $deleteTableColumnAtSelection,
  $deleteTableRowAtSelection,
  $findTableNode,
  $insertTableColumnAtSelection,
  $insertTableRowAtSelection,
  $isTableCellNode,
  $isTableSelection,
  $setTableColumnIsHeader,
  $setTableRowIsHeader,
  TableCellHeaderStates,
  type TableCellNode,
  type TableNode,
} from "@lexical/table";
import { $findMatchingParent, $insertNodeToNearestRoot } from "@lexical/utils";
import {
  $createParagraphNode,
  $getRoot,
  $getSelection,
  $isParagraphNode,
  $isRangeSelection,
  $isRootNode,
  type NodeKey,
} from "lexical";
import { tableLimits, type DocumentLimits } from "../../core/limits";
import type { TableEdit, TableRefusal } from "./commands";

export type TableResult = { ok: true } | { ok: false; reason: TableRefusal };

const REFUSED_TARGET: TableResult = { ok: false, reason: "invalid-target" };
const REFUSED_SIZE: TableResult = { ok: false, reason: "too-large" };

/** The table both ends of the selection are in, whether it is a caret, text, or cells. */
function $selectedTable(): TableNode | null {
  const selection = $getSelection();
  if (!$isRangeSelection(selection) && !$isTableSelection(selection)) return null;
  const table = $findTableNode(selection.anchor.getNode());
  if (table === null) return null;
  return table.is($findTableNode(selection.focus.getNode())) ? table : null;
}

const $sizeOf = (table: TableNode): { rows: number; columns: number } => {
  const [grid] = $computeTableMapSkipCellCheck(table, null, null);
  return { rows: grid.length, columns: grid[0]?.length ?? 0 };
};

const hasHeader = (cell: TableCellNode | null, bit: number): boolean =>
  cell !== null && (cell.getHeaderStyles() & bit) !== 0;

/** What the controls need to know about the table the selection is in. */
export interface TableState {
  tableKey: NodeKey;
  /** The cell the selection's focus is in: where the controls attach. */
  cellKey: NodeKey;
  rows: number;
  columns: number;
  headerRow: boolean;
  headerColumn: boolean;
  /** False at `maxTableRows` and `maxTableColumns`. */
  canInsertRow: boolean;
  canInsertColumn: boolean;
  /** How many rows and columns the selection touches, for "Delete 2 rows". */
  selectedRows: number;
  selectedColumns: number;
}

export function $tableState(limits: DocumentLimits): TableState | null {
  const table = $selectedTable();
  const selection = $getSelection();
  if (table === null || selection === null) return null;
  if (!$isRangeSelection(selection) && !$isTableSelection(selection)) return null;
  const focus = $findMatchingParent(selection.focus.getNode(), $isTableCellNode);
  const anchor = $findMatchingParent(selection.anchor.getNode(), $isTableCellNode);
  if (focus === null || anchor === null) return null;
  const [grid, focusAt, anchorAt] = $computeTableMapSkipCellCheck(table, focus, anchor);
  if (focusAt === null || anchorAt === null) return null;
  const rows = grid.length;
  const columns = grid[0]?.length ?? 0;
  const max = tableLimits(limits);
  // The first row, or column, is a header when its far end is one too: the corner cell alone
  // belongs to both and says nothing about either.
  return {
    tableKey: table.getKey(),
    cellKey: focus.getKey(),
    rows,
    columns,
    headerRow: hasHeader(grid[0]?.[columns - 1]?.cell ?? null, TableCellHeaderStates.ROW),
    headerColumn: hasHeader(grid[rows - 1]?.[0]?.cell ?? null, TableCellHeaderStates.COLUMN),
    canInsertRow: rows < max.rows,
    canInsertColumn: columns < max.columns,
    selectedRows: Math.abs(focusAt.startRow - anchorAt.startRow) + 1,
    selectedColumns: Math.abs(focusAt.startColumn - anchorAt.startColumn) + 1,
  };
}

function $ensureNotEmpty(): void {
  const root = $getRoot();
  if (root.getChildrenSize() === 0) root.append($createParagraphNode()).selectStart();
}

/**
 * Inserts an empty table at the selection and puts the caret in its first cell. An empty paragraph
 * is where a table is usually asked for (the slash menu leaves one); the table goes in front of
 * it, so the paragraph keeps its identity and there is a line to type on after the table. Anywhere
 * else the block is split at the caret, as for a divider.
 */
export function $insertTable(
  size: { rows: number; columns: number; headerRow: boolean },
  limits: DocumentLimits,
): TableResult {
  const selection = $getSelection();
  if (!$isRangeSelection(selection)) return REFUSED_TARGET;
  const anchor = selection.anchor.getNode();
  // No tables in tables.
  if ($findTableNode(anchor) !== null) return REFUSED_TARGET;
  const { rows, columns } = size;
  if (!Number.isInteger(rows) || !Number.isInteger(columns) || rows < 1 || columns < 1) {
    return REFUSED_TARGET;
  }
  const max = tableLimits(limits);
  if (rows > max.rows || columns > max.columns) return REFUSED_SIZE;

  const table = $createTableNodeWithDimensions(rows, columns, {
    rows: size.headerRow,
    columns: false,
  });
  const block = anchor.getTopLevelElement();
  if (
    selection.isCollapsed() &&
    $isParagraphNode(block) &&
    $isRootNode(block.getParent()) &&
    block.isEmpty()
  ) {
    block.insertBefore(table);
  } else {
    $insertNodeToNearestRoot(table);
  }
  // A table at the very end leaves nowhere to click to write after it.
  if (table.getNextSibling() === null) table.insertAfter($createParagraphNode());
  table.getFirstDescendant()?.selectStart();
  return { ok: true };
}

/** Applies an edit to the table the selection is in, and leaves the selection somewhere usable. */
export function $editTable(edit: TableEdit, limits: DocumentLimits): TableResult {
  const table = $selectedTable();
  if (table === null) return REFUSED_TARGET;
  const size = $sizeOf(table);
  const max = tableLimits(limits);
  switch (edit.type) {
    case "insert-row":
      if (size.rows >= max.rows) return REFUSED_SIZE;
      $insertTableRowAtSelection(edit.where === "below");
      return { ok: true };
    case "insert-column":
      if (size.columns >= max.columns) return REFUSED_SIZE;
      $insertTableColumnAtSelection(edit.where === "after");
      return { ok: true };
    case "delete-row":
      // Upstream removes the table with its last row, and moves the selection out first.
      $deleteTableRowAtSelection();
      $ensureNotEmpty();
      return { ok: true };
    case "delete-column":
      $deleteTableColumnAtSelection();
      $ensureNotEmpty();
      return { ok: true };
    case "set-header-row":
      $setTableRowIsHeader(table, 0, edit.header);
      return { ok: true };
    case "set-header-column":
      $setTableColumnIsHeader(table, 0, edit.header);
      return { ok: true };
    case "delete-table": {
      const next = table.getNextSibling();
      const previous = table.getPreviousSibling();
      table.remove();
      $ensureNotEmpty();
      if (next !== null) next.selectStart();
      else if (previous !== null) previous.selectEnd();
      return { ok: true };
    }
  }
}
